/**
 * Getting back into an account.
 *
 * Two screens that share a shell: asking for a link, and using one.
 *
 * The request screen says the same thing whether or not the address is on file,
 * because the server behaves the same way. "No account with that email" is a
 * convenience for the owner and a way for anybody else to ask which companies
 * use this — and this particular customer would rather that question had no
 * answer.
 */

import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { link, navigate } from '../router';
import { Alert, Button, Field, Input } from '../ui';
import { ArrowRightIcon, CircleCheckIcon, Icon, Wordmark } from '../icons';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-5 py-12">
      <a {...link('/')} className="mb-8 flex items-center gap-2 font-medium tracking-tight">
        <span className="text-accent">
          <Wordmark size={20} />
        </span>
        Vouchkeep
      </a>
      {children}
    </div>
  );
}

export function ForgotView() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      // Only rate limiting can land here; the route itself never says whether
      // the address exists.
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell>
        <div className="card w-full max-w-sm p-6 text-center">
          <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent-wash text-accent">
            <Icon icon={CircleCheckIcon} size={18} isAnimated={false} />
          </span>
          <h1 className="text-[17px]">Check your inbox</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            If there is an account for <b className="text-ink">{email}</b>, a reset link is on its
            way. It works once and expires in an hour.
          </p>
          <p className="mt-4 text-[12.5px] leading-relaxed text-faint">
            We say “if” on purpose. Confirming which addresses have accounts would let anyone ask
            which companies use this.
          </p>
          <a {...link('/login')} className="mt-6 block">
            <Button variant="secondary" className="w-full">
              Back to sign in
            </Button>
          </a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="card w-full max-w-sm p-6">
        <h1 className="text-[19px]">Reset your password</h1>
        <p className="mt-1 text-[13px] text-muted">
          We will email a link that signs you in once, so you can set a new one.
        </p>

        <div className="mt-6">
          <Field label="Email" error={error}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </Field>
        </div>

        <Button
          type="submit"
          variant="primary"
          icon={ArrowRightIcon}
          busy={busy}
          className="mt-6 w-full"
        >
          Send the link
        </Button>

        <p className="mt-4 text-center text-[13px] text-muted">
          Remembered it?{' '}
          <a {...link('/login')} className="text-accent">
            Sign in
          </a>
        </p>
      </form>
    </Shell>
  );
}

export function ResetView({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError('Those two do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <div className="card w-full max-w-sm p-6">
          <h1 className="text-[17px]">That link is incomplete</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            Some mail clients cut long links. Ask for a fresh one and open it in a browser.
          </p>
          <a {...link('/forgot')} className="mt-5 block">
            <Button variant="primary" className="w-full">
              Send a new link
            </Button>
          </a>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="card w-full max-w-sm p-6 text-center">
          <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent-wash text-accent">
            <Icon icon={CircleCheckIcon} size={18} isAnimated={false} />
          </span>
          <h1 className="text-[17px]">Password changed</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            Every other session on the account was signed out, including any that were not yours.
          </p>
          <Button variant="primary" className="mt-6 w-full" onClick={() => navigate('/login')}>
            Sign in
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="card w-full max-w-sm p-6">
        <h1 className="text-[19px]">Set a new password</h1>
        <p className="mt-1 text-[13px] text-muted">This link works once.</p>

        <div className="mt-6 space-y-4">
          <Field label="New password" hint="At least 12 characters.">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
            />
          </Field>
          <Field label="Again">
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
        </div>

        {error ? (
          <div className="mt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          icon={ArrowRightIcon}
          busy={busy}
          className="mt-6 w-full"
        >
          Change it
        </Button>

        <p className="mt-4 text-center text-[12.5px] leading-relaxed text-faint">
          Signing in again afterwards is expected: changing the password ends every session, which is
          the point of doing it.
        </p>
      </form>
    </Shell>
  );
}
